import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import 'react-datepicker/dist/react-datepicker.css';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { RadioButton } from '../common/Form';

interface HideScheduleModalProps {
  error?: string;
  isOpen: boolean;
  onCancel: Function;
  onConfirm: (reappearAt?: string) => void | Promise<void>;
}

// Every hide action starts on "Indefinitely" with the date/time unset -- deliberately not pre-filled
// from any existing reappearAt when re-opening on an already-hidden item, so there's no UTC-to-
// restaurant-local-wall-clock round trip to get wrong here. Pick a fresh date/time (or none) each time.
const HideScheduleModal = ({ error, isOpen, onCancel, onConfirm }: HideScheduleModalProps) => {
  const [isScheduled, setIsScheduled] = useState(false);
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);

  if (!isOpen) {
    return null;
  }

  const canSave = !isScheduled || (date != null && time != null);

  const handleSave = () => {
    if (!isScheduled) {
      onConfirm();
      return;
    }

    if (date == null || time == null) {
      return;
    }

    const reappearAt = DateTime.fromJSDate(date)
      .set({ hour: time.getHours(), minute: time.getMinutes(), second: 0, millisecond: 0 })
      .toISO({ includeOffset: false });

    onConfirm(reappearAt);
  };

  return (
    <Modal className="hide-schedule-modal">
      <div className="modal-header">
        <h1 className="modal-title">HIDE MENU ITEM (OR MODIFER)</h1>
      </div>
      <div className="modal-body">
        <div className="modal-error">{error}</div>
        <label className="hide-schedule-option">
          <RadioButton name="hideScheduleMode" active={!isScheduled} onToggle={() => setIsScheduled(false)} />
          Indefinitely
        </label>
        <label className="hide-schedule-option">
          <RadioButton name="hideScheduleMode" active={isScheduled} onToggle={() => setIsScheduled(true)} />
          Until
          {isScheduled && (
            <span className="hide-schedule-until-inputs">
              <DatePicker
                selected={date}
                onChange={(value: Date) => setDate(value)}
                dateFormat="MM/dd/yyyy"
                popperPlacement="bottom-start"
                placeholderText="Calendar"
                minDate={new Date()}
                className="hide-schedule-date-input"
              />
              <DatePicker
                selected={time}
                onChange={(value: Date) => setTime(value)}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={15}
                timeCaption="Time"
                dateFormat="h:mm aa"
                popperPlacement="bottom-start"
                placeholderText="Time"
                className="hide-schedule-time-input"
              />
            </span>
          )}
        </label>
      </div>
      <div className="modal-footer">
        <Button onClick={onCancel} className="modal-cancel-button">
          CANCEL
        </Button>
        <Button onClick={handleSave} className="modal-confirm-button" isDisabled={!canSave}>
          SAVE
        </Button>
      </div>
    </Modal>
  );
};

export default HideScheduleModal;
